import { Divider, TextField } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import type { DynamicInvoiceMeasurementChartData,
  DynamicInvoiceMeasurementChartMeter,
  DynamicInvoiceMeasurementChartMeterUsage } from '@weft/api-definition';
import type { ProductLineItem } from '@weft/services';
import { isLineItemWithUnits } from '@weft/services';
import type { LineItemProduct, Period } from '@weft/types';
import { getOptionalObjectProperty,
  isDateBetween,
  isNullOrUndefined,
  type Optional,
  validateNotNullOrUndefined } from '@weft/utils';
import React, { useState } from 'react';
import { formatDate } from '../../../../../../utils/date';
import { UsageLineItemBody } from '../../UsageLineItemBody/UsageLineItemBody';

type DynamicInvoiceProductLineItemUsageCollapseBodyProps = {
  lineItem: ProductLineItem;
  customerId: string;
  measurementChartData: DynamicInvoiceMeasurementChartData;
};

export const DynamicInvoiceProductLineItemUsageCollapseBody: React.FC<DynamicInvoiceProductLineItemUsageCollapseBodyProps> = ({
  lineItem,
  customerId,
  measurementChartData,
}) => {
  const [selectedLineItemProductVariant, setSelectedLineItemProductVariant] = useState<string>(getSelectedLineItemProductVariantFormat(0));
  const { lineItemProduct } = lineItem;

  if (!isLineItemWithUnits(lineItem)) {
    return null;
  }

  if (lineItemProduct.variants.some(lineItemProductVariant => isNullOrUndefined(lineItemProductVariant.periodLimit))) {
    return (
      <>
        <Divider/>

        <UsageLineItemBody
          productLineItem={lineItem}
          lineItemProduct={lineItemProduct}
          units={lineItem.units}
          customerId={customerId}
          productMetersMeasurementChartData={getOptionalObjectProperty(measurementChartData, lineItemProduct.productId!)}
        />
      </>
    );
  }

  return (
    <>
      <Divider/>

      <TextField
        select
        label="Period"
        size="small"
        value={selectedLineItemProductVariant}
        onChange={(event) => setSelectedLineItemProductVariant(event.target.value)}
        sx={{
          minWidth: 'max-content',
          margin: '20px 24px 0px 24px',
        }}
      >
        {lineItemProduct.variants.map((lineItemProductVariant, index) => (
          <MenuItem
            key={getSelectedLineItemProductVariantFormat(index)}
            value={getSelectedLineItemProductVariantFormat(index)}
          >
            {`${formatDate(lineItemProductVariant.periodLimit?.startTime!)} - ${formatDate(lineItemProductVariant.periodLimit?.endTime!)}`}
          </MenuItem>
        ))}
      </TextField>

      <UsageLineItemBody
        productLineItem={lineItem}
        lineItemProduct={{
          ...lineItemProduct,
          variants: [lineItemProduct.variants.find((_lineItemProductVariant, index) => selectedLineItemProductVariant === getSelectedLineItemProductVariantFormat(index))!],
        }}
        units={lineItem.units}
        customerId={customerId}
        productMetersMeasurementChartData={adjustProductMetersMeasurementChartData(lineItemProduct, selectedLineItemProductVariant, measurementChartData)}
      />
    </>
  );
};

function adjustProductMetersMeasurementChartData(
  lineItemProduct: LineItemProduct,
  selectedLineItemProductVariant: string,
  measurementChartData: DynamicInvoiceMeasurementChartData,
): Optional<DynamicInvoiceMeasurementChartMeter> {
  const lineItemProductVariant = lineItemProduct.variants.find((_lineItemProductVariant, index) => selectedLineItemProductVariant === getSelectedLineItemProductVariantFormat(index));
  validateNotNullOrUndefined(lineItemProductVariant);

  const productMetersMeasurementChartData = getOptionalObjectProperty(measurementChartData, lineItemProduct.productId!);
  if (isNullOrUndefined(productMetersMeasurementChartData)) {
    return undefined;
  }

  const { meter, periodLimit } = lineItemProductVariant;

  validateNotNullOrUndefined(meter);
  validateNotNullOrUndefined(meter.meterId);
  validateNotNullOrUndefined(periodLimit);

  const meterMeasurementChartData = getOptionalObjectProperty(productMetersMeasurementChartData, meter.meterId)!;
  const adjustedMeterUsageData = adjustMeterUsageData(meterMeasurementChartData, periodLimit);

  return {
    [meter.meterId]: adjustedMeterUsageData,
  };
}

function adjustMeterUsageData(
  meterMeasurementChartData: DynamicInvoiceMeasurementChartMeterUsage,
  periodLimit: Period,
): DynamicInvoiceMeasurementChartMeterUsage {
  const meterUsageData = Object.entries(meterMeasurementChartData).filter(([stringifiedDateInMilliseconds]) => {
    return isDateBetween(new Date(parseInt(stringifiedDateInMilliseconds)), periodLimit);
  });

  return Object.fromEntries(meterUsageData);
}

function getSelectedLineItemProductVariantFormat(index: number) {
  return `lineItemProductVariant-${index}`;
}